import { AfterViewInit, ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { VideoService } from '../../services/video/video.service';
import { VideoResponseDto } from '../../common/general-types/videos.dto';
import { ViewSubManageService } from './services/view-sub-manage/view-sub-manage.service';
import { firstValueFrom, of, Subscription, take } from 'rxjs';
import { CreatorService } from '../../services/creator/creator.service';
import { CreatorResponseDto } from '../../common/general-types/creators.dto';
import { CentralManagementService } from '../../services/central-management/central-management.service';
import { BriefApplicationService } from '../../services/brief-application/brief-application.service';
import { BriefApplicationResponseDto, BriefProgressEnum, BriefResponseDto, ApplicationStatusEnum, SocialChannelEnum } from '../../common/general-types/briefs.dto';
import { summariseBriefAsk } from '@/app/common/brief-ask/summarise-brief-ask';
import { socialChannelIdsOnPieces, videosRequired } from '@/app/common/brief-pieces/piece-rules';
import { briefIsUgcOnly } from './molecules/video-feedback/video-feedback.results';
import { DeclineBriefApplicationDto } from '../../common/general-types/brief-application.dto';
import { VideoStatusEnum } from '../../common/general-types/videos.dto';
import { BriefService } from '../../services/brief/brief.service';
import { StatusHistoryComponent } from './molecules/status-history/status-history.component';
import { CurrencyPipe } from '@angular/common';
import { PayoutDto, PayoutFilter, PayoutTypeEnum } from '../../common/general-types/payouts.dto';
import { PayoutsService } from '../../services/payouts/payouts.service';
import { DraftItemThumbComponent } from './molecules/draft-item-thumb/draft-item-thumb.component';
import { VideoFeedbackComponent } from './molecules/video-feedback/video-feedback.component';
import { ImageService } from '../../services/image/image.service';
import { LdgImgComponent } from '../../atoms/ldg-img/ldg-img.component';
import { GetStreamService, STREAM_CHANNEL_ALREADY_EXISTS_MESSAGE } from '../../services/get-stream/get-stream.service';
import { ToastNotificationService } from '../../atoms/toast-notification/toast-notification.service';
import { CampaignService } from '../../services/campaign/campaign.service';
import { CustomTemplatesService, StreamChatModule, StreamTextareaModule } from 'stream-chat-angular';
import { LoadingInlineComponent } from '../../atoms/loading-inline/loading-inline.component';
import { UploadDraftComponent } from './molecules/upload-draft/upload-draft.component';
import { S3uploaderService } from '../../services/s3uploader/s3uploader.service';
import { MagicSearchService } from '../creators/service/magic-search/magic-search.service';
import { CreatorProfileComponent } from '../creators/molecules/creator-profile/creator-profile.component';
import { PayBonus } from './molecules/pay-bonus/pay-bonus';
import { ManageBriefTabsService } from '../manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { ReviewModal } from './molecules/review-modal/review-modal';
import { AuthService, UserAccessRolesEnum } from '../../services/auth/auth.service';
import { PaymentStatus } from './molecules/payment-status/payment-status';
import { HasRoleDirectiveDirective } from '@/app/common/directives/has-role/has-role-directive.directive';
import { AffiliateService } from '../../services/affiliate/affiliate.service';
import { AccountService } from '../../services/account/account.service';
import { ShoutPlusBriefService } from '../../shout-plus/services/shout-plus-brief/shout-plus-brief.service';
import { RakutenAffiliateLinkApiDto, RakutenService } from '../../shout-plus/services/rakuten/rakuten.service';
import { AffiliateAssignmentApiDto } from '../../shared/models/affiliate';
import { CreateBriefPaymentMethodDto, PaymentMethodTypeEnum } from '../../common/general-types/shout-plus.dto';
import { RemunerationTypeIconComponent } from '../../shout-plus/atoms/remuneration-type-icon/remuneration-type-icon.component';
import { ShopifyAffiliateAssignmentComponent } from '../../shout-plus/molecules/shopify-affiliate-assignment/shopify-affiliate-assignment.component';
import { RakutenAffiliateAssignmentComponent } from '../../shout-plus/molecules/rakuten-affiliate-assignment/rakuten-affiliate-assignment.component';
import { buildAdminSettingsViewSubmissionLink } from '../admin-options/settings/services/admin-settings-context.service';
import { CreatorDistribution } from './molecules/creator-distribution/creator-distribution';
import { BuyAdLicenseModal } from './molecules/buy-ad-license-modal/buy-ad-license-modal';
import { AdLicenseService } from '../../services/ad-license/ad-license.service';
import { AdLicenseDto, AdLicenseQuoteDto } from '../../common/general-types/ad-license.dto';
import { indexLicensesByVideo, isLicensedOrPending } from '../../services/ad-license/ad-license-state';
import { AlertService } from '../../atoms/alert/alert.service';
import { WalletService } from '../../services/wallet/wallet.service';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { briefCarriesAdLicense } from '../../common/brief-pieces/ad-license';

/** Pseudo status id for the "Licensed" filter option (pending + active licences). */
const LICENSED_FILTER_ID = -2;


@Component({
  selector: 'app-view-submission',
  standalone: true,
  imports: [StatusHistoryComponent, CurrencyPipe, DraftItemThumbComponent,
    VideoFeedbackComponent, LdgImgComponent, StreamChatModule,
    StreamTextareaModule, LoadingInlineComponent, UploadDraftComponent,
    CreatorProfileComponent, PayBonus, ReviewModal, PaymentStatus, HasRoleDirectiveDirective, RemunerationTypeIconComponent,
    ShopifyAffiliateAssignmentComponent, RakutenAffiliateAssignmentComponent, CreatorDistribution, BuyAdLicenseModal],
  templateUrl: './view-submission.component.html',
  styleUrl: './view-submission.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSubmissionComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('shopifyAffiliateDrawer') shopifyAffiliateDrawer?: ShopifyAffiliateAssignmentComponent;
  @ViewChild('rakutenAffiliateDrawer') rakutenAffiliateDrawer?: RakutenAffiliateAssignmentComponent;
  @ViewChild('emptyChatTemplate') emptyChatTemplate?: TemplateRef<void>;

  bonusActiveStatuses: number[] = [4, 5, 6, 7];
  allStatusOptions: any[] = [
    { id: VideoStatusEnum.All, name: 'All' },
    { id: VideoStatusEnum.New, name: 'New' },
    { id: VideoStatusEnum.Seen, name: 'Seen' },
    { id: VideoStatusEnum.Accepted, name: 'Approved' },
    // { id: VideoStatusEnum.Declined, name: 'Declined' }
  ];
  /**
   * A cross-post the brand had committed to was cancelled from the distribution card.
   *
   * The card updates itself optimistically; this keeps the page's own copy of the
   * application in step so anything else reading `crossPlatformsAccepted` agrees.
   */
  onCrossPlatformCancelled(socialChannel: number): void {
    const application = this.focussedApplication();
    if (!application) {
      return;
    }
    this.focussedApplication.set({
      ...application,
      crossPlatformsAccepted: (application.crossPlatformsAccepted ?? []).filter(
        (channel) => Number(channel) !== Number(socialChannel),
      ),
    });
  }

  /** A VideoStatusEnum value, or LICENSED_FILTER_ID for the "Licensed" option. */
  selectedStatusFilter = signal<number>(VideoStatusEnum.All);
  selectedStatusFilterLabel = signal<string>('All');
  protected readonly VideoStatusEnum = VideoStatusEnum;

  focussedBriefId = signal<string | undefined>(undefined);
  focussedBrief = signal<BriefResponseDto | undefined>(undefined);
  creatorId = signal<string | undefined>(undefined);
  creator = signal<CreatorResponseDto | undefined>(undefined);

  applicationVideos = signal<VideoResponseDto[] | undefined>(undefined);
  displayVideos = signal<VideoResponseDto[] | undefined>(undefined);

  focussedApplication = signal<BriefApplicationResponseDto | undefined>(undefined);

  /** Bonus Variables */
  bonusPaid = signal<boolean>(false);
  bonusAmount = signal<number>(0);
  bonusCurrency = signal<string>('USD');

  payoutsData = signal<PayoutDto[] | undefined>(undefined);
  showPayonusModal = signal<boolean>(false);
  /** Bonus Variables - END */

  /** Chat Variables */
  chatLoadingError = signal<boolean>(false);
  joiningChat = signal<boolean>(false);
  /** True only after this component instance's channel has been confirmed active. */
  chatViewReady = signal<boolean>(false);
  /** Chat Variables - END */

  showVideoFeedback = signal<VideoResponseDto | undefined>(undefined);

  /** Ad License Variables */
  adLicenseQuote = signal<AdLicenseQuoteDto | null>(null);
  licensesByVideoId = signal<Record<string, AdLicenseDto>>({});
  licenseModalVideo = signal<VideoResponseDto | undefined>(undefined);

  /**
   * UGC-only brief bought without the up-front licence: the only kind of brief where a
   * one-off licence can be bought per video.
   */
  isUgcNoLicense = computed(() => {
    const brief = this.focussedBrief();
    return !!brief && !brief.shoutBrief && !briefCarriesAdLicense(brief);
  });

  /** The quote when licences can be bought, otherwise null so every licence slot hides. */
  eligibleLicenseQuote = computed(() => {
    const quote = this.adLicenseQuote();
    return quote?.eligible ? quote : null;
  });

  statusOptions = computed<{ id: number; name: string }[]>(() => {
    const options: { id: number; name: string }[] = [...this.allStatusOptions];
    if (this.eligibleLicenseQuote() || Object.keys(this.licensesByVideoId()).length > 0) {
      options.push({ id: LICENSED_FILTER_ID, name: 'Licensed' });
    }
    return options;
  });
  /** Ad License Variables - END */

  subscriptionList = new Subscription();

  showReviewModal = signal<boolean>(false);

  isDownloadingAll = signal<boolean>(false);
  private readonly approvedVideosList = computed(
    () => this.applicationVideos()?.filter(v => v.status === VideoStatusEnum.Accepted) ?? []
  );
  approvedVideoCount = computed(() => this.approvedVideosList().length);

  hasAffiliateAssignment = signal(false);
  affiliateAssignmentLoading = signal(false);
  affiliateAssignmentRecord = signal<AffiliateAssignmentApiDto | null>(null);
  rakutenAffiliateLink = signal<RakutenAffiliateLinkApiDto | null>(null);

  private readonly affiliateDrawerApplicationStatuses: ApplicationStatusEnum[] = [
    ApplicationStatusEnum.Hired,
    ApplicationStatusEnum.Started,
    ApplicationStatusEnum.Completed,
  ];

  showAffiliateDrawerButton = computed(() => this.isAffiliateBrief() && this.isAffiliateDrawerApplicationStatus());

  protected readonly PaymentMethodTypeEnum = PaymentMethodTypeEnum;

  /** When true, in-flight Stream chat helpers must skip UI updates / follow-up actions. Set in `ngOnDestroy`. */
  private chatViewDisposed = false;
  private chatTeardownAbortController = new AbortController();
  /** Prevents automatic code-17 recovery from calling join more than once per load. */
  private chatJoinRetryUsed = false;
  /** Prevents overlapping create/join/watch runs for this view instance. */
  private chatConnectInFlight = false;
  
  /** Services */
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  private videoService = inject(VideoService);
  private viewSubManageService = inject(ViewSubManageService);
  private creatorService = inject(CreatorService);
  public centralManagement = inject(CentralManagementService);
  private briefApplicationService = inject(BriefApplicationService);
  private briefService = inject(BriefService);
  private payoutsService = inject(PayoutsService);
  public imageService = inject(ImageService);
  public getStreamService = inject(GetStreamService);
  public toastNotificationService = inject(ToastNotificationService);
  private campaignService = inject(CampaignService);
  public magicSearchService = inject(MagicSearchService);
  private uploadService = inject(S3uploaderService);
  private authService = inject(AuthService);
  private affiliateService = inject(AffiliateService);
  private accountService = inject(AccountService);
  private shoutPlusBriefService = inject(ShoutPlusBriefService);
  private rakutenService = inject(RakutenService);
  private customTemplatesService = inject(CustomTemplatesService);
  private adLicenseService = inject(AdLicenseService);
  private alertService = inject(AlertService);
  private walletService = inject(WalletService);
  private currentUserService = inject(CurrentUserService);
  /** Services - END */

  constructor() {
    effect(() => {
      const unHireApplicationFromSignal = this.briefApplicationService.unHireApplicationSignal();
      if (unHireApplicationFromSignal) {
        this.handleUnHireApplication(unHireApplicationFromSignal);
      }
    });
  }

  ngOnInit(): void {
    const hasBonusRole = this.authService.userRoles.some(
      role => role === UserAccessRolesEnum.AccountManager || role === UserAccessRolesEnum.CreatorAdmin,
    );
    this.bonusActiveStatuses = hasBonusRole ? [4, 5, 6, 7, 8] : [4, 5, 6, 7];

    const params$ = this.route.paramMap ?? of(this.route.snapshot.paramMap);
    this.subscriptionList.add(
      params$.subscribe((params) => {
        const briefId = params.get('briefId') || undefined;
        const creatorId = params.get('creatorId') || undefined;
        if (briefId === this.focussedBriefId() && creatorId === this.creatorId()) {
          return;
        }

        this.resetChatForRouteChange();
        this.focussedBriefId.set(briefId);
        this.creatorId.set(creatorId);
        if (briefId && creatorId) {
          this.initViewSubmission();
        }
      }),
    );
  }

  ngAfterViewInit(): void {
    if (this.emptyChatTemplate) {
      this.customTemplatesService.emptyMainMessageListPlaceholder$.next(this.emptyChatTemplate);
    }
  }

  initViewSubmission() {
    this.getAssociatedContent();
    void this.getCreator();
  }

  getAssociatedContent() {
    console.log('⭐️ getAssociatedContent() --> ');
    console.log('⭐️ getAssociatedContent() --> focussedBriefId: ', this.focussedBriefId());

    if (this.creatorId() && this.focussedBriefId()) {
      const subVideos = this.videoService.getCreatorBriefVideos(this.creatorId()!, this.focussedBriefId()!).subscribe(foundContent => {
        console.log('⭐️⭐️⭐️ getAssociatedContent() --> foundContent: ', foundContent);
        this.applicationVideos.set(foundContent);
        this.displayVideos.set(JSON.parse(JSON.stringify(foundContent)));
        console.log('🤢🤢🤢 Application Videos: ', this.applicationVideos());

        this.viewSubManageService.approvedVideos.set(foundContent.filter(v => v.status === 2));

      }, error => {
        console.warn('getAssociatedContentError: ', error);
      });
      this.subscriptionList.add(subVideos);
    }
  }

  getCreator() {
    console.log('⭐️ getCreator() --> ');
    console.log('⭐️ getCreator() --> creatorId: ', this.creatorId());
    if (this.creatorId()) {
      const subCreator = this.creatorService.getCreatorById(this.creatorId()!).subscribe((creator: any) => {
        this.creator.set(creator);

        console.log('🤢🤢🤢 GOT Creator: ', this.creator);

        if (this.centralManagement.focussedApplicant() && !this.centralManagement.focussedApplicant()!.creator) {
          this.centralManagement.focussedApplicant()!['creator'] = this.creator()!;
        }

        this.getBriefApplication();

      }, (error: any) => {
        console.warn('getCreatorError: ', error);
      });
      this.subscriptionList.add(subCreator);
    }
  }

  async getBriefApplication() {
    if (this.focussedBriefId() && this.creatorId()) {

      const subApplication = this.briefApplicationService.findMyApplicationWithBrief(this.focussedBriefId()!, this.creatorId()!).subscribe({
        next: (application: any) => {
          console.log('Brief Application: ', application);

          console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨 APPLICATION: ', application);

          this.focussedBrief.set(application.brief);
          this.briefApplicationService.currentApplicationSignal.set(application);
          this.briefService.focussedBriefSignal.set(application.brief);
          this.briefService.currentBriefSignal.set(application.brief);
          console.log('🤢🤢🤢 FOCUSED BRIEF: ', this.focussedBrief());
          this.viewSubManageService.requiredVideos.set(
            videosRequired({
              pieces: application.brief?.pieces,
              videosPerCreator: application.brief?.videosPerCreator,
              owed: application.hireRecord?.owed,
            }),
          );

          this.focussedApplication.set(application);
          if (!this.centralManagement.focussedApplicant()) {
            this.centralManagement.focussedApplicant.set(application);
            this.centralManagement.focussedApplicant()!['creator'] = this.creator()!;
          }
          if (this.focussedApplication()) {
            this.getStreamThread(this.focussedApplication()!.id);
          }
          this.checkForBonus();
          this.loadAdLicenses();
          this.subscriptionList.add(
            this.manageBriefTabsService.syncBriefPaymentMethod(application.brief).subscribe({
              next: () => this.loadAffiliateAssignment(),
              error: () => this.loadAffiliateAssignment(),
            }),
          );
        },
        error: error => {
          console.warn('getBriefApplicationError: ', error);
        }
      });
      this.subscriptionList.add(subApplication);
    }
  }

  loadAffiliateAssignment(): void {
    const briefId = this.focussedBriefId();
    const creatorId = this.creatorId();
    if (!briefId || !creatorId) {
      return;
    }

    this.affiliateAssignmentRecord.set(null);
    this.rakutenAffiliateLink.set(null);
    this.hasAffiliateAssignment.set(false);
    this.affiliateAssignmentLoading.set(true);

    const provider = this.resolveAffiliateProvider();

    if (provider === 'rakuten') {
      this.fetchRakutenAffiliateAssignment();
      return;
    }

    if (provider === 'website') {
      this.fetchWebsiteAffiliateAssignment();
      return;
    }

    this.fetchShopifyAffiliateAssignment(this.resolveShopifyProviderAccountId());
  }

  private isAffiliateBrief(): boolean {
    const pm = this.getResolvedBriefPaymentMethod();
    if (pm?.type === PaymentMethodTypeEnum.AFFILIATE) {
      return true;
    }
    // Legacy / non-SHOUT+ affiliate briefs may only set the flag.
    return !!this.focussedBrief()?.enableAffiliateMarketing;
  }

  private isAffiliateDrawerApplicationStatus(): boolean {
    const status = this.focussedApplication()?.applicationStatus;
    return status != null && this.affiliateDrawerApplicationStatuses.includes(status);
  }

  private getResolvedBriefPaymentMethod(): CreateBriefPaymentMethodDto | null {
    const brief = this.focussedBrief() as BriefResponseDto & {
      brief_payment_method?: CreateBriefPaymentMethodDto | null;
      briefPaymentMethod?: CreateBriefPaymentMethodDto | null;
    } | undefined;
    const embedded =
      (brief?.brief_payment_method as CreateBriefPaymentMethodDto | null | undefined)
      ?? (brief?.briefPaymentMethod as CreateBriefPaymentMethodDto | null | undefined);
    return embedded ?? this.shoutPlusBriefService.briefPaymentMethodSignal();
  }

  private resolveShopifyProviderAccountId(): string | undefined {
    const biz = this.focussedBrief()?.businessAccount as {
      shopifyShopDomain?: string | null;
      shopifyAccount?: { shopifyShopDomain?: string | null };
    } | undefined;
    const brand = this.accountService.selectedBrandSignal() as {
      shopifyShopDomain?: string | null;
      shopifyAccount?: { shopifyShopDomain?: string | null };
    } | null;
    const domain =
      biz?.shopifyShopDomain
      ?? biz?.shopifyAccount?.shopifyShopDomain
      ?? brand?.shopifyShopDomain
      ?? brand?.shopifyAccount?.shopifyShopDomain;
    const trimmed = domain?.trim();
    return trimmed ? trimmed.toLowerCase() : undefined;
  }

  private fetchShopifyAffiliateAssignment(
    providerAccountId: string | undefined,
    allowRetryWithoutShop = true,
  ): void {
    const briefId = this.focussedBriefId();
    const creatorId = this.creatorId();
    if (!briefId || !creatorId) {
      return;
    }

    this.subscriptionList.add(
      this.affiliateService.getAssignmentForBriefAndCreator({
        briefId,
        creatorId,
        provider: 'shopify',
        providerAccountId,
      }).subscribe({
        next: (result) => {
          if (result && typeof result === 'object' && result.id) {
            this.affiliateAssignmentRecord.set(result);
            this.hasAffiliateAssignment.set(true);
            this.affiliateAssignmentLoading.set(false);
            return;
          }

          if (providerAccountId && allowRetryWithoutShop) {
            this.fetchShopifyAffiliateAssignment(undefined, false);
            return;
          }

          this.affiliateAssignmentRecord.set(null);
          this.hasAffiliateAssignment.set(false);
          this.affiliateAssignmentLoading.set(false);
        },
        error: () => {
          if (providerAccountId && allowRetryWithoutShop) {
            this.fetchShopifyAffiliateAssignment(undefined, false);
            return;
          }
          this.affiliateAssignmentRecord.set(null);
          this.hasAffiliateAssignment.set(false);
          this.affiliateAssignmentLoading.set(false);
        },
      }),
    );
  }

  /** Website (SHOUT pixel) assignment — same shape as Shopify, different provider. */
  private fetchWebsiteAffiliateAssignment(): void {
    const briefId = this.focussedBriefId();
    const creatorId = this.creatorId();
    if (!briefId || !creatorId) {
      this.affiliateAssignmentLoading.set(false);
      return;
    }

    this.subscriptionList.add(
      this.affiliateService.getAssignmentForBriefAndCreator({
        briefId,
        creatorId,
        provider: 'website',
      }).subscribe({
        next: (result) => {
          if (result && typeof result === 'object' && result.id && result.trackingUrl?.trim()) {
            this.affiliateAssignmentRecord.set(result);
            this.hasAffiliateAssignment.set(true);
          } else {
            this.affiliateAssignmentRecord.set(null);
            this.hasAffiliateAssignment.set(false);
          }
          this.affiliateAssignmentLoading.set(false);
        },
        error: () => {
          this.affiliateAssignmentRecord.set(null);
          this.hasAffiliateAssignment.set(false);
          this.affiliateAssignmentLoading.set(false);
        },
      }),
    );
  }

  /** Prefer unified affiliate_assignment; fall back to legacy rakuten_affiliate_link row. */
  private fetchRakutenAffiliateAssignment(): void {
    const briefId = this.focussedBriefId();
    const creatorId = this.creatorId();
    if (!briefId || !creatorId) {
      this.affiliateAssignmentLoading.set(false);
      return;
    }

    this.subscriptionList.add(
      this.affiliateService.getAssignmentForBriefAndCreator({
        briefId,
        creatorId,
        provider: 'rakuten',
      }).subscribe({
        next: (result) => {
          const trackingUrl = result?.trackingUrl?.trim();
          if (result && typeof result === 'object' && result.id && trackingUrl) {
            this.affiliateAssignmentRecord.set(result);
            this.hasAffiliateAssignment.set(true);
            this.affiliateAssignmentLoading.set(false);
            return;
          }
          this.fetchLegacyRakutenAffiliateLink(briefId, creatorId);
        },
        error: () => {
          this.fetchLegacyRakutenAffiliateLink(briefId, creatorId);
        },
      }),
    );
  }

  private fetchLegacyRakutenAffiliateLink(briefId: string, creatorId: string): void {
    this.subscriptionList.add(
      this.rakutenService.getAffiliateLink(briefId, creatorId).subscribe({
        next: (result) => {
          const link = result?.trackingLink?.trim();
          if (link) {
            this.rakutenAffiliateLink.set(result);
            this.hasAffiliateAssignment.set(true);
          } else {
            this.rakutenAffiliateLink.set(null);
            this.hasAffiliateAssignment.set(false);
          }
          this.affiliateAssignmentLoading.set(false);
        },
        error: () => {
          this.rakutenAffiliateLink.set(null);
          this.hasAffiliateAssignment.set(false);
          this.affiliateAssignmentLoading.set(false);
        },
      }),
    );
  }

  private resolveAffiliateProvider(): 'shopify' | 'rakuten' | 'website' {
    const pm = this.getResolvedBriefPaymentMethod() as {
      affiliate_provider?: string | null;
      affiliateProvider?: string | null;
    } | null;
    const affiliateProvider = (pm?.affiliate_provider ?? pm?.affiliateProvider ?? '')
      .trim()
      .toLowerCase();
    if (affiliateProvider === 'rakuten') {
      return 'rakuten';
    }
    if (affiliateProvider === 'website') {
      return 'website';
    }
    return 'shopify';
  }

  openAffiliateDrawer(): void {
    if (this.affiliateAssignmentLoading()) {
      this.toastNotificationService.alert({
        title: 'Loading assignment',
        message: 'Affiliate assignment is still loading. Try again in a moment.',
        type: 'info',
        icon: 'info',
      });
      return;
    }

    const provider = this.resolveAffiliateProvider();
    const row = this.affiliateAssignmentRecord();

    // Unified path: both Shopify and Rakuten use affiliate_assignment when present.
    if (row?.id) {
      const opened = this.openShopifyStyleAssignmentDrawer(row, provider);
      if (!opened) {
        this.toastNotificationService.alert({
          title: 'Drawer unavailable',
          message: 'Affiliate assignment drawer could not be opened. Refresh and try again.',
          type: 'warning',
          icon: 'warning',
        });
      }
      return;
    }

    if (provider === 'rakuten') {
      const link = this.rakutenAffiliateLink()?.trackingLink?.trim();
      if (!link) {
        // One more attempt — load may have been skipped or raced.
        this.loadAffiliateAssignment();
        this.toastNotificationService.alert({
          title: 'No Rakuten link',
          message: 'No Rakuten affiliate link is available for this creator yet. Reloading…',
          type: 'warning',
          icon: 'warning',
        });
        return;
      }
      if (!this.rakutenAffiliateDrawer) {
        this.toastNotificationService.alert({
          title: 'Drawer unavailable',
          message: 'Rakuten affiliate drawer could not be opened. Refresh and try again.',
          type: 'warning',
          icon: 'warning',
        });
        return;
      }
      this.rakutenAffiliateDrawer.open({
        briefTitle: this.focussedBrief()?.title ?? null,
        trackingLink: link,
      });
      return;
    }

    const providerLabel = provider === 'website' ? 'website' : 'Shopify';
    this.toastNotificationService.alert({
      title: 'No assignment',
      message: `No ${providerLabel} affiliate assignment is available for this creator yet.`,
      type: 'warning',
      icon: 'warning',
    });
  }

  private openShopifyStyleAssignmentDrawer(
    row: AffiliateAssignmentApiDto,
    provider: 'shopify' | 'rakuten' | 'website',
  ): boolean {
    if (!this.shopifyAffiliateDrawer) {
      return false;
    }

    const creator = this.creator();
    const creatorName =
      creator?.firstName != null && creator?.lastName != null
        ? `${creator.firstName} ${creator.lastName}`
        : null;

    const providerLabel =
      provider === 'rakuten' ? 'Rakuten' : provider === 'website' ? 'Website' : 'Shopify';

    this.shopifyAffiliateDrawer.open({
      id: row.id,
      providerLabel,
      briefTitle: this.focussedBrief()?.title ?? null,
      creatorName,
      briefId: row.briefId ?? this.focussedBriefId()!,
      creatorId: row.creatorId ?? this.creatorId()!,
      trackingUrl: row.trackingUrl ?? null,
      discountCode: row.discountCode ?? null,
      commissionPercent: row.commissionPercent ?? null,
      commissionValue: row.commissionValue ?? null,
      expiresAt: row.expiresAt ?? null,
      clicks: row.clicks ?? null,
      timesUsed: row.timesUsed ?? null,
      revenue: row.revenue ?? null,
      currencyCode: row.currencyCode ?? null,
      createdDate: row.createdDate ?? null,
    });
    return true;
  }

  async getStreamThread(applicationId: string) {
    if (this.chatViewDisposed || this.chatTeardownAbortController.signal.aborted || this.chatConnectInFlight) {
      return;
    }

    this.chatConnectInFlight = true;

    console.log('getStreamThread', applicationId);
    console.log('User IDs: ', this.focussedBrief());
    console.log('Creator: ', this.creator());

    const abortSignal = this.chatTeardownAbortController.signal;
    /** Must run after `finally` clears `chatConnectInFlight`, or `joinStreamChat` no-ops. */
    let shouldAutoJoinExistingChannel = false;

    try {
      const cmUids: string[] = (await firstValueFrom(this.campaignService.getCampaignManagerUids(this.focussedBrief()!.campaignId!))) as string[];
      console.log('CM UIDs: ', cmUids);

      if (this.chatViewDisposed || abortSignal.aborted) {
        return;
      }

      const userIds = [...cmUids, this.focussedApplication()!.creatorUserId];

      try {
        const success = await this.getStreamService.getStreamThreadById(
          applicationId,
          userIds,
          `${this.creator()!.firstName} ${this.creator()!.lastName.charAt(0)} - ${this.focussedBrief()!.title}`,
          this.focussedBriefId()!,
          this.focussedBrief()!.title,
          this.creatorId()!,
          this.focussedBrief()!.campaignId!,
          abortSignal,
        );

        if (this.chatViewDisposed || abortSignal.aborted) {
          return;
        }

        if (!success) {
          this.chatLoadingError.set(true);
          this.toastNotificationService.alert({
            title: 'Failed to connect to chat channel',
            message: 'Please try again.',
            type: 'error',
            icon: 'error'
          });
        } else {
          console.log('🟢🟢🟢 Stream thread created successfully');
          this.markChatViewReady(applicationId);
        }
      } catch (getStreamThreadErr: unknown) {
        if (this.chatViewDisposed || ViewSubmissionComponent.isAbortDomError(getStreamThreadErr)) {
          return;
        }
        console.warn('❌❌❌ 👋👋👋 getStreamThreadErr: ', getStreamThreadErr);
        this.joiningChat.set(false);
        if (
          getStreamThreadErr instanceof Error &&
          getStreamThreadErr.message === STREAM_CHANNEL_ALREADY_EXISTS_MESSAGE
        ) {
          // Channel exists but this user cannot ReadChannel until added server-side.
          if (!this.chatViewDisposed && !this.chatJoinRetryUsed) {
            shouldAutoJoinExistingChannel = true;
          } else if (!this.chatViewDisposed) {
            this.chatLoadingError.set(true);
          }
        }
      }

    } catch (cmUidsErr: unknown) {
      if (this.chatViewDisposed || ViewSubmissionComponent.isAbortDomError(cmUidsErr)) {
        return;
      }
      console.warn('cmUidsErr: ', cmUidsErr);
    } finally {
      this.chatConnectInFlight = false;
    }

    if (shouldAutoJoinExistingChannel && !this.chatViewDisposed) {
      void this.joinStreamChat();
    }
  }

  private markChatViewReady(applicationId: string): void {
    this.joiningChat.set(false);
    this.chatLoadingError.set(false);
    this.getStreamService.pinMessagingChannel(applicationId);
    // Defer one macro-task so stream-chat-angular's synchronous
    // initialisation settles before <stream-channel> renders,
    // preventing NG0100 and ensuring only this instance's channel shows.
    setTimeout(() => {
      void this.finishChatViewReady(applicationId);
    }, 0);
  }

  private async finishChatViewReady(applicationId: string): Promise<void> {
    if (this.chatViewDisposed) {
      return;
    }
    if (!this.getStreamService.isPinnedChannelActive(applicationId)) {
      await this.getStreamService.restorePinnedMessagingChannel();
    }
    if (this.chatViewDisposed) {
      return;
    }
    if (this.getStreamService.isPinnedChannelActive(applicationId)) {
      this.chatViewReady.set(true);
      return;
    }
    this.chatLoadingError.set(true);
  }

  async watchStreamThread(applicationId: string): Promise<void> {
    if (this.chatViewDisposed || this.chatTeardownAbortController.signal.aborted) {
      return;
    }

    const abortSignal = this.chatTeardownAbortController.signal;

    try {
      const success = await this.getStreamService.watchStreamThreadById(applicationId, abortSignal);

      if (this.chatViewDisposed || abortSignal.aborted) {
        return;
      }

      if (success) {
        this.markChatViewReady(applicationId);
      } else {
        this.joiningChat.set(false);
        this.chatLoadingError.set(true);
      }
    } catch (watchErr: unknown) {
      if (this.chatViewDisposed || ViewSubmissionComponent.isAbortDomError(watchErr)) {
        return;
      }
      console.warn('watchStreamThreadErr: ', watchErr);
      this.joiningChat.set(false);
      this.chatLoadingError.set(true);
    }
  }

  checkForBonus() {
    if (this.focussedApplication() && this.focussedApplication()!.statusHistory) {
      this.bonusPaid.set(this.focussedApplication()!.statusHistory.some(item => item.status === 12));

      console.log('🤢🤢🤢 Bonus Paid: ', this.bonusPaid());
      if (this.bonusPaid()) {

        const bonusFilters: PayoutFilter = {
          briefApplicationId: this.focussedApplication()!.id,
          type: PayoutTypeEnum.Bonus
        }

        void this.getBonusDetails(bonusFilters);
      }
    }
  }

  getBonusDetails(filters: PayoutFilter) {

    this.payoutsService.getFilteredPayoutsV2(filters).pipe(take(1)).subscribe({
      next: (response: PayoutDto[]) => {
        this.payoutsData.set(response);

        this.payoutsData.set(response);
        console.log('🧩🧩🧩 ViewSubmissionV3 - getBonusDetails() - payoutsData: 🧩🧩🧩 ', response);

        let amountPennies: number = 0;
        response.forEach(payout => {
          amountPennies += payout.amount;
        });
        this.bonusCurrency.set(response[0].currencyCode.toUpperCase());
        this.bonusAmount.set(amountPennies / 100);

        console.log('🧩🧩🧩 ViewSubmissionV3 - getBonusDetails() - payoutsData: 🧩🧩🧩 ', response);
      },
      error: error => {
        console.error('Error fetching bonus details:', error);
      }
    });
  }

  async approveVideo(video: VideoResponseDto) {
    // console.log('🤢🤢🤢 Approve Video()', video);
    // console.log('🤢🤢🤢 Approved Videos', this.viewSubManageService.approvedVideos()?.length);
    // console.log('🤢🤢🤢 videosPerCreator', this.focussedBrief()?.videosPerCreator);
    // void this.videoService.approveVideoModern(video.id, this.focussedBriefId()!).subscribe({
    //   next: (response: any) => {
    //     console.log('🤢🤢🤢 Approve Video Response: ', response);
    //   },
    //   error: error => {
    //     console.error('Error approving video:', error);
    //   }
    // });
    // this.getAssociatedContent();
  }

  payBonus() {
    console.log('⭐️ payBonus() --> ');
    this.showPayonusModal.set(true);
  }
  async joinStreamChat() {
    if (this.chatViewDisposed || this.chatConnectInFlight) {
      return;
    }

    this.chatConnectInFlight = true;
    this.chatJoinRetryUsed = true;
    this.joiningChat.set(true);

    console.log('joinStreamChat');

    try {
      const channelJoined = await this.getStreamService.addMeToChannel(this.focussedApplication()!.id);
      console.log('channelJoined: ', channelJoined);

      if (this.chatViewDisposed || this.chatTeardownAbortController.signal.aborted) {
        return;
      }

      if (channelJoined == false) {
        this.joiningChat.set(false);
        this.chatLoadingError.set(true);
        this.toastNotificationService.alert({ title: 'Error adding you to the chat', message: 'Please try again.', type: 'error', icon: 'error' });
      } else {
        console.log('Woohoo!! 🥳🥳🥳🥳🥳 channelJoined: ', channelJoined);
        await this.watchStreamThread(this.focussedApplication()!.id);
      }
    } catch (joinChannelErr: unknown) {
      if (!(this.chatViewDisposed || ViewSubmissionComponent.isAbortDomError(joinChannelErr))) {
        console.error('joinChannelErr: ', joinChannelErr);
        this.toastNotificationService.alert({ title: 'Error adding you to the chat', message: 'Please try again.', type: 'error', icon: 'error' });
      }
      if (!this.chatViewDisposed) {
        this.joiningChat.set(false);
        this.chatLoadingError.set(true);
      }
    } finally {
      this.chatConnectInFlight = false;
    }
  }

  ngOnDestroy(): void {
    this.chatViewDisposed = true;
    this.chatViewReady.set(false);
    this.chatTeardownAbortController.abort();
    this.joiningChat.set(false);
    this.customTemplatesService.emptyMainMessageListPlaceholder$.next(undefined);
    this.subscriptionList.unsubscribe();
    this.getStreamService.unpinMessagingChannel(this.focussedApplication()?.id);
    this.getStreamService.deselectActiveChannel();
    this.viewSubManageService.approvedVideos.set([]);
    this.viewSubManageService.requiredVideos.set(0);
  }

  private resetChatForRouteChange(): void {
    this.chatTeardownAbortController.abort();
    this.chatTeardownAbortController = new AbortController();
    this.chatViewReady.set(false);
    this.chatLoadingError.set(false);
    this.joiningChat.set(false);
    this.chatJoinRetryUsed = false;
    this.chatConnectInFlight = false;
    this.getStreamService.unpinMessagingChannel(this.focussedApplication()?.id);
    this.getStreamService.deselectActiveChannel();
    this.creator.set(undefined);
    this.applicationVideos.set(undefined);
    this.displayVideos.set(undefined);
    this.focussedApplication.set(undefined);
    this.focussedBrief.set(undefined);
  }

  backToManageBrief() {
    this.getStreamService.deselectActiveChannel();
    const briefId = this.focussedBriefId();
    if (briefId) {
      const ctx = this.manageBriefTabsService.consumeViewSubmissionReturnContext(briefId);
      if (ctx) {
        if (ctx.tab === BriefProgressEnum.Assignment && ctx.subTab > 0) {
          void this.router.navigate(['/manage-brief', ctx.briefId, ctx.tab, ctx.subTab]);
        } else {
          void this.router.navigate(['/manage-brief', ctx.briefId, ctx.tab]);
        }
        return;
      }
    }
    this.location.back();
  }

  handleUnHireApplication(declineObject: DeclineBriefApplicationDto): void {
    const applicationId = declineObject.application?.id;
    this.briefApplicationService.unHireApplicationSignal.set(null);
    if (!applicationId) {
      return;
    }

    const subUnHire = this.briefApplicationService
      .adminSetApplicationStatus(applicationId, declineObject)
      .subscribe({
        next: (response) => {
          if (response) {
            const updatedApplication = response as BriefApplicationResponseDto;
            const current = this.focussedApplication();
            if (current?.id === updatedApplication.id) {
              this.focussedApplication.set({
                ...current,
                ...updatedApplication,
                applicationStatus:
                  updatedApplication.applicationStatus ?? ApplicationStatusEnum.HiredRemoved,
              });
            }

            this.toastNotificationService.alert({
              title: `${declineObject.application?.creator?.firstName}'s creator successfully un-hired!`,
              message: 'The creator has been successfully un-hired',
              type: 'success',
              icon: 'success',
            });
          }
          this.backToManageBrief();
        },
        error: (err) => {
          this.toastNotificationService.alert({
            title: `${declineObject.application?.creator?.firstName}'s un-hire failed!`,
            message: 'An error occurred while un-hiring the creator',
            type: 'error',
            icon: 'error',
          });
          console.error('Error - application un-hire', err);
        },
      });
    this.subscriptionList.add(subUnHire);
  }

  /** Copies the current page URL and opens brief tracker with that URL prefilled. */
  copyUrlAndOpenAdminSettings(): void {
    const isAdmin = this.authService.userRoles.includes(UserAccessRolesEnum.CreatorAdmin);
    if (!isAdmin) {
      return;
    }

    const viewSubmissionUrl = window.location.href;
    const settingsPath = buildAdminSettingsViewSubmissionLink(viewSubmissionUrl);

    void navigator.clipboard.writeText(viewSubmissionUrl).then(() => {
      this.toastNotificationService.alert({
        title: 'URL copied',
        message: 'Opening admin settings…',
        type: 'success',
        icon: 'success',
      });
    });

    void this.router.navigateByUrl(settingsPath);
  }

  selectStatusFilter(status: { id: number, name: string }) {
    console.log('⭐️ selectStatusFilter() --> status: ', status);
    this.selectedStatusFilter.set(status.id);
    this.selectedStatusFilterLabel.set(this.statusOptions().find(option => option.id === status.id)?.name || 'All Videos');

    if (status.id === VideoStatusEnum.All) {
      this.displayVideos.set(JSON.parse(JSON.stringify(this.applicationVideos())));
    } else if (status.id === LICENSED_FILTER_ID) {
      const licenses = this.licensesByVideoId();
      this.displayVideos.set(this.applicationVideos()?.filter(video => isLicensedOrPending(licenses[video.id])) || []);
    } else {
      this.displayVideos.set(this.applicationVideos()?.filter(video => video.status === status.id) || []);
    }

    console.log('After selectedBriefStatus() -> ', this.selectedStatusFilter())
    console.log('After label() -> ', this.selectedStatusFilterLabel())
  }

  /** Ad License: quote + latest licence per video. Only UGC-only briefs without a licence qualify. */
  loadAdLicenses(): void {
    const application = this.focussedApplication();
    if (!application || !this.isUgcNoLicense()) {
      this.adLicenseQuote.set(null);
      this.licensesByVideoId.set({});
      return;
    }
    this.subscriptionList.add(
      this.adLicenseService.getForApplication(application.id).subscribe({
        next: state => {
          this.adLicenseQuote.set(state.quote);
          this.licensesByVideoId.set(indexLicensesByVideo(state.licenses ?? []));
        },
        error: (err: unknown) => {
          console.warn('loadAdLicenses error: ', err);
          this.adLicenseQuote.set(null);
        },
      }),
    );
  }

  /** "Draft N": 1-based position among this creator's videos, oldest first (matches the API). */
  draftLabel(video: VideoResponseDto): string {
    const ordered = [...(this.applicationVideos() ?? [])].sort(
      (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime(),
    );
    const index = ordered.findIndex(v => v.id === video.id);
    return index >= 0 ? `Draft ${index + 1}` : 'Draft';
  }

  openLicenseModal(video: VideoResponseDto): void {
    if (!this.eligibleLicenseQuote()) return;
    this.licenseModalVideo.set(video);
  }

  onLicenseRequested(license: AdLicenseDto): void {
    this.setLicense(license);
  }

  async cancelLicense(license: AdLicenseDto): Promise<void> {
    const confirmed = await this.alertService.confirm({
      title: 'Cancel licence request?',
      message: `${this.creator()?.firstName ?? 'The creator'} won't be able to accept it, and the full amount goes back to your wallet.`,
      confirmText: 'Cancel request',
      cancelText: 'Keep request',
      type: 'confirm',
      icon: 'warning',
    });
    if (!confirmed) return;

    this.subscriptionList.add(
      this.adLicenseService.cancel(license.id).subscribe({
        next: response => {
          this.setLicense(response.license);
          const brandId = this.currentUserService.focussedBrand?.id;
          if (brandId) {
            this.walletService.getBalance(brandId).subscribe({
              error: (err: unknown) => console.warn('Could not refresh wallet balance', err),
            });
          }
          this.toastNotificationService.alert({
            title: 'Request cancelled',
            message: 'The licence request was withdrawn and your wallet was refunded.',
            type: 'success',
            icon: 'success',
          });
        },
        error: (err: unknown) => {
          console.warn('cancelLicense error: ', err);
          // The creator may have answered in the meantime, so pull the real state.
          this.loadAdLicenses();
          this.toastNotificationService.alert({
            title: 'Could not cancel',
            message: 'The request may already have been answered. We refreshed its status.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  private setLicense(license: AdLicenseDto): void {
    this.licensesByVideoId.update(existing => ({ ...existing, [license.videoId]: license }));
    if (this.selectedStatusFilter() === LICENSED_FILTER_ID) {
      this.selectStatusFilter({ id: LICENSED_FILTER_ID, name: 'Licensed' });
    }
  }

  getBadgeColour(brief: any) {
    const ask = summariseBriefAsk(brief ?? {});
    if (ask.source === 'pieces') {
      return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
    }
    if (brief.shoutBrief) {
      switch (brief.shoutBrief.socialPlatform) {
        case 0:
          return 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 ring-orange-600 ring-opacity-20';
        case 1:
          return 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 ring-sky-600 ring-opacity-20';
        case 2:
          return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
        default:
          return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
      }
    } else {

      if (briefCarriesAdLicense(brief)) {
        return 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 ring-pink-600 ring-opacity-20';
      } else {
        return 'bg-primary/10 dark:bg-primary/25 text-accent ring-primary/20';
      }
    }
  }

  getPostType() {
    const ask = summariseBriefAsk(this.briefService.focussedBriefSignal() ?? {});
    if (ask.source === 'pieces') {
      return ask.label;
    }
    if (this.briefService.focussedBriefSignal() && this.briefService.focussedBriefSignal()?.shoutBrief) {
      switch (this.briefService.focussedBriefSignal()?.shoutBrief?.socialPlatform) {
        case SocialChannelEnum['TikTok Post']:
          return 'TikTok Post';
        case SocialChannelEnum['Instagram Reel']:
          return 'Instagram Reel';
        case SocialChannelEnum['Instagram Story']:
          return 'Instagram Story';
        case SocialChannelEnum['YouTube Shorts']:
          return 'YouTube Shorts';
        case SocialChannelEnum['Snapchat Spotlight']:
          return 'Snapchat Spotlight';
        case SocialChannelEnum['Snapchat Story']:
          return 'Snapchat Story';
        default:
          return 'Social Post';
      }
    } else if (briefCarriesAdLicense(this.briefService.focussedBriefSignal())) {
      return 'Ad Content';
    } else {
      return 'UGC Only';
    }
  }

  getIsUgcOnly(): boolean {
    return briefIsUgcOnly(this.briefService.focussedBriefSignal());
  }

  launchPortfolio() {
    this.magicSearchService.portfolioCreatorId.set(this.creator()!.blazeFirebaseUid);
    this.magicSearchService.portfolioCreator.set(this.creator()!);
  }

  onVideoUploaded(video: VideoResponseDto) {
    console.log('🤢🤢🤢 On Video Uploaded()', video);
    this.applicationVideos.set([...(this.applicationVideos() || []), { ...video, status: VideoStatusEnum.New }]);
    this.displayVideos.set([...(this.displayVideos() || []), { ...video, status: VideoStatusEnum.New }]);
  }

  private static isAbortDomError(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'AbortError';
  }

  async downloadAllApproved() {
    const videos = this.approvedVideosList();
    if (!videos.length || this.isDownloadingAll()) return;
    this.isDownloadingAll.set(true);
    for (const video of videos) {
      if (video.fullUrl.includes('firebasestorage.googleapis.com')) {
        this.uploadService.progressFirebaseDownload(video.fullUrl);
      } else {
        this.uploadService.secureVideoDownload(video.fullUrl, video.videoName);
      }
      await new Promise<void>(resolve => setTimeout(resolve, 400));
    }
    this.isDownloadingAll.set(false);
  }

  reviewSubmitted(response: any) {
    console.log('reviewSubmitted: ', response);
    this.showReviewModal.set(false);
  }

  videoApproved(approved: boolean) {
    console.log('videoApproved: ', approved);
    if (approved) {
      /**
       * 1. Check if we have as many approved videos as the required videos
       * 2. If yes -> Show the review modal
       */

      const required = videosRequired({
        pieces: this.focussedBrief()?.pieces,
        videosPerCreator: this.focussedBrief()?.videosPerCreator,
        owed: this.focussedApplication()?.hireRecord?.owed,
      });
      const accepted = this.displayVideos()?.filter(video => video.status === VideoStatusEnum.Accepted).length ?? 0;
      if (required > 0 && accepted === required) {
        this.showVideoFeedback.set(undefined);
        this.showReviewModal.set(true);
      }
      else {
        // this.toastNotificationService.alert({ title: 'Not enough approved videos', message: 'Please approve more videos to submit a review.', type: 'error', icon: 'error' });
      }
    }
  }

  openLatestVideoFeedback(): void {
    const videos = this.applicationVideos();
    if (!videos?.length) {
      return;
    }

    const latest = [...videos].sort(
      (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
    )[0];
    this.showVideoFeedback.set(latest);
  }

  /** Piece-list briefs and legacy cross-post briefs both need the channel panel. */
  showsPlacementPanel(): boolean {
    const brief = this.focussedBrief();
    if (!brief) {
      return false;
    }
    if ((brief.pieces?.length ?? 0) > 0) {
      return socialChannelIdsOnPieces(brief.pieces ?? []).length > 0;
    }
    return (brief.shoutBrief?.crossPlatforms?.length ?? 0) > 0;
  }

  getWidthBasedOnCrossPlatforms() {
    if (this.showsPlacementPanel()) {
      return 'col-span-1 lg:col-span-4 2xl:col-span-6';
    } else {
      return 'col-span-2 lg:col-span-8 2xl:col-span-9';
    }
  }
}
